import { useState, useEffect, useCallback, useRef } from 'react';
import { APP_ORIGIN } from '../utils/appUrl';
import api from '../api';
import Toast from '../components/Toast';
import CowAvatar from '../components/CowAvatar';
import CowLoader from '../components/CowLoader';

const BANCOS = [
  'Banco de Chile', 'Banco Santander', 'BancoEstado', 'Banco BCI',
  'Banco Falabella', 'Banco Ripley', 'Scotiabank', 'Banco Security',
  'Banco Itaú', 'Banco BICE', 'Banco Consorcio', 'Coopeuch',
  'Tenpo', 'Mercado Pago', 'MACH', 'Otro',
];

const TIPOS_CUENTA = [
  'Cuenta Corriente', 'Cuenta Vista', 'Cuenta de Ahorro', 'Cuenta RUT',
];

function parseBankData(text) {
  const t = text.toLowerCase();
  const result = {};
  for (const banco of BANCOS) {
    if (t.includes(banco.toLowerCase())) { result.banco = banco; break; }
  }
  for (const tipo of TIPOS_CUENTA) {
    if (t.includes(tipo.toLowerCase())) { result.tipoCuenta = tipo; break; }
  }
  const rutMatch = text.match(/\b(\d{1,2}[.\d]*\d)-([0-9kK])\b/);
  if (rutMatch) result.rut = rutMatch[0].replace(/\./g, '');
  const numMatch = text.match(/(?:cuenta|n[°º]?|número)[:\s#]*(\d{4,20})/i) || text.match(/\b(\d{7,20})\b/);
  if (numMatch) result.numeroCuenta = numMatch[1];
  return Object.keys(result).length >= 2 ? result : null;
}

export default function Profile() {
  const [user, setUser]                     = useState(null);
  const [name, setName]                     = useState('');
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [seed, setSeed]                     = useState(null);
  const [banco, setBanco]                   = useState('');
  const [tipoCuenta, setTipo]               = useState('');
  const [numeroCuenta, setNum]              = useState('');
  const [rut, setRut]                       = useState('');
  const [saving, setSaving]                 = useState(false);
  const [toast, setToast]                   = useState(null);
  const [pasteSuggestion, setPasteSuggestion] = useState(null);
  const [friendLinkCopied, setFriendLinkCopied] = useState(false);
  const bankSectionRef = useRef(null);

  const showToast = (message, type = 'success') => setToast({ message, type });
  const clearToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    api.get('/users/me').then(r => {
      const d = r.data;
      setUser(d);
      setName(d.name || '');
      setNombreCompleto(d.nombreCompleto || '');
      const stored = parseInt(d.profilePicture);
      setSeed(isNaN(stored) ? d.id : stored);
      setBanco(d.banco || '');
      setTipo(d.tipoCuenta || '');
      setNum(d.numeroCuenta || '');
      setRut(d.rut || '');
    });
  }, []);

  const randomize = () => setSeed(Math.floor(Math.random() * 999999) + 1);

  const handleSave = async () => {
    if (!name.trim()) { showToast('El username no puede estar vacío', 'error'); return; }
    setSaving(true);
    try {
      const res = await api.put('/users/me', {
        name, profilePicture: String(seed),
        nombreCompleto, banco, tipoCuenta, numeroCuenta, rut,
      });
      setUser(res.data);
      localStorage.setItem('userName', res.data.name);
      localStorage.setItem('avatarSeed', String(seed));
      showToast('Perfil actualizado');
    } catch {
      showToast('Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBankFocus = async () => {
    if (pasteSuggestion) return;
    try {
      const text = await navigator.clipboard.readText();
      if (!text || text.length < 10) return;
      const parsed = parseBankData(text);
      if (parsed) setPasteSuggestion(parsed);
    } catch { }
  };

  const applyPaste = () => {
    if (!pasteSuggestion) return;
    if (pasteSuggestion.banco)        setBanco(pasteSuggestion.banco);
    if (pasteSuggestion.tipoCuenta)   setTipo(pasteSuggestion.tipoCuenta);
    if (pasteSuggestion.numeroCuenta) setNum(pasteSuggestion.numeroCuenta);
    if (pasteSuggestion.rut)          setRut(pasteSuggestion.rut);
    setPasteSuggestion(null);
    showToast('Datos bancarios pegados', 'info');
  };

  if (!user || seed === null) return <CowLoader text="Cargando perfil..." />;

  return (
    <div className="profile-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}

      <div className="profile-card">
        <h2 className="profile-title">Mi Perfil</h2>

        {/* Avatar */}
        <div className="avatar-section">
          <CowAvatar seed={seed} size={100} />
          <button className="randomize-btn" onClick={randomize}>🎲 Randomizar vaca</button>
        </div>

        <div className="divider" />

        {/* Link de amigo */}
        <div className="section-label">Mi link de amigo</div>
        <p className="section-hint">Compártelo y te agregarán directo como amigo.</p>
        <div className="friend-link-row">
          <span className="friend-link-url">{APP_ORIGIN}/u/{btoa(String(user.id))}</span>
          <button
            className="friend-link-copy-btn"
            onClick={() => {
              const url = `${APP_ORIGIN}/u/${btoa(String(user.id))}`;
              if (navigator.clipboard) navigator.clipboard.writeText(url);
              else { const el = document.createElement('textarea'); el.value = url; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); }
              setFriendLinkCopied(true);
              setTimeout(() => setFriendLinkCopied(false), 2000);
            }}
          >
            {friendLinkCopied ? '¡Copiado! ✓' : '🔗 Copiar link'}
          </button>
        </div>

        <div className="divider" />

        {/* Info básica */}
        <div className="section-label">Cuenta</div>
        <div className="field-grid">
          <div className="field">
            <label>Username</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              placeholder="Tu nombre de usuario"
            />
          </div>
          <div className="field">
            <label>Correo</label>
            <input type="email" value={user.email} disabled />
          </div>
        </div>

        <div className="divider" />

        {/* Datos bancarios */}
        <div className="section-label">Datos bancarios</div>
        <p className="section-hint">Para que te paguen fácil. Se comparten cuando alguien te debe plata.</p>

        <div className="bank-section" ref={bankSectionRef} onFocus={handleBankFocus}>
          {pasteSuggestion && (
            <div className="paste-bar">
              <span>📋 Datos bancarios detectados en portapapeles</span>
              <div className="paste-actions">
                <button onClick={applyPaste}>Pegar</button>
                <button className="dismiss" onClick={() => setPasteSuggestion(null)}>✕</button>
              </div>
            </div>
          )}

          <div className="field-grid">
            <div className="field">
              <label>Nombre completo</label>
              <input
                type="text"
                value={nombreCompleto}
                onChange={e => setNombreCompleto(e.target.value)}
                placeholder="Como aparece en tu banco"
              />
            </div>
            <div className="field">
              <label>Correo de transferencia</label>
              <input type="email" value={user.email} disabled />
            </div>
          </div>

          <div className="field-grid">
            <div className="field">
              <label>Banco</label>
              <select value={banco} onChange={e => setBanco(e.target.value)}>
                <option value="">Selecciona</option>
                {BANCOS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Tipo de cuenta</label>
              <select value={tipoCuenta} onChange={e => setTipo(e.target.value)}>
                <option value="">Selecciona</option>
                {TIPOS_CUENTA.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="field-grid">
            <div className="field">
              <label>Número de cuenta</label>
              <input
                type="text"
                value={numeroCuenta}
                onChange={e => setNum(e.target.value.replace(/\D/g, ''))}
                placeholder="12345678"
              />
            </div>
            <div className="field">
              <label>RUT</label>
              <input
                type="text"
                value={rut}
                onChange={e => setRut(e.target.value)}
                placeholder="12345678-9"
                maxLength={12}
              />
            </div>
          </div>
        </div>

        <button className="save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>

      <style jsx>{`
        .profile-container {
          padding: 2rem 1rem;
          max-width: 560px;
          margin: 0 auto;
        }

        .profile-card {
          background: var(--bg-secondary);
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px var(--shadow-color);
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .profile-title {
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
          text-align: center;
        }

        /* Avatar */
        .avatar-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }

        .randomize-btn {
          background: transparent;
          border: 1.5px solid var(--border-color);
          color: var(--text-secondary);
          padding: 0.45rem 1.1rem;
          border-radius: 20px;
          font-size: 0.88rem;
          cursor: pointer;
          transition: all 0.15s;
        }

        .randomize-btn:hover {
          border-color: var(--accent-color);
          color: var(--accent-color);
        }

        /* Divider */
        .divider {
          height: 1px;
          background: var(--border-color);
          margin: 0;
        }

        /* Section labels */
        .section-label {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--text-muted);
          margin-bottom: -0.5rem;
        }

        .section-hint {
          font-size: 0.82rem;
          color: var(--text-muted);
          margin: -0.75rem 0 0;
          line-height: 1.4;
        }

        /* Field grid */
        .field-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }

        .field {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
        }

        .field label {
          font-size: 0.82rem;
          font-weight: 600;
          color: var(--text-secondary);
        }

        .field input,
        .field select {
          padding: 0.65rem 0.85rem;
          border: 1.5px solid var(--border-color);
          border-radius: 8px;
          font-size: 0.95rem;
          background: var(--bg-primary);
          color: var(--text-primary);
          transition: border-color 0.2s;
          width: 100%;
          box-sizing: border-box;
        }

        .field input:focus,
        .field select:focus {
          outline: none;
          border-color: var(--accent-color);
        }

        .field input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Bank section */
        .bank-section {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-top: -0.25rem;
        }

        /* Paste bar */
        .paste-bar {
          background: var(--accent-color);
          color: white;
          border-radius: 8px;
          padding: 0.65rem 1rem;
          font-size: 0.85rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .paste-actions {
          display: flex;
          gap: 0.4rem;
          align-items: center;
        }

        .paste-actions button {
          background: white;
          color: var(--accent-color);
          border: none;
          padding: 0.3rem 0.8rem;
          border-radius: 6px;
          font-size: 0.82rem;
          font-weight: 700;
          cursor: pointer;
        }

        .paste-actions button.dismiss {
          background: transparent;
          color: white;
          font-size: 1rem;
          padding: 0.2rem 0.4rem;
          opacity: 0.8;
        }

        /* Friend link */
        .friend-link-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: var(--bg-primary);
          border: 1.5px solid var(--border-color);
          border-radius: 10px;
          padding: 0.65rem 0.85rem;
          margin-top: -0.25rem;
        }

        .friend-link-url {
          flex: 1;
          font-size: 0.8rem;
          color: var(--text-muted);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .friend-link-copy-btn {
          background: var(--accent-color);
          color: white;
          border: none;
          padding: 0.4rem 0.85rem;
          border-radius: 7px;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
          transition: opacity 0.15s;
        }

        .friend-link-copy-btn:hover { opacity: 0.85; }

        /* Save button */
        .save-btn {
          background: var(--accent-color);
          color: white;
          border: none;
          padding: 0.9rem;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 0.25rem;
        }

        .save-btn:hover:not(:disabled) {
          background: var(--accent-hover);
          transform: translateY(-1px);
        }

        .save-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .field-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
